/**
 * Layout — the authenticated app shell (replaces the old top-only AppHeader).
 *
 *   - Persistent left SIDEBAR (desktop ≥md): logo, Home, the provisioned tools,
 *     and a collapsible "Admin" group (real admins only).
 *   - TOP BAR: mobile logo, display name, sign out.
 *   - Fixed mobile BOTTOM TAB BAR (<md) — Home + tools + Admin.
 *
 * Design system v1 (Sep 2026): sidebar on `bg-sidebar` with grouped navigation
 * (People, Work, Finance, Revenue & insights, Admin), Lucide icons through
 * design/Icon, the active item in DAAily plum (`brand-subtle` + a 2px `brand`
 * bar), the theme-switched lockup, and the user menu (profile, theme, sign
 * out). Per-section sub-navigation lives in <SectionTabs/>, not here.
 *
 * Nav visibility derives from `state.user.tools.*.enabled` (tools) and
 * `state.isAdmin` (admin group) — the SAME sources the route guards use, so
 * the two stay in lockstep.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Building2, PanelLeftClose, PanelLeftOpen, type LucideIcon } from 'lucide-react';
import { Icon } from '../design/Icon';
import { useAppNav, AppNavContext, ICONS, type NavItem } from './useAppNav';
import { CommandPalette, SearchButton } from './CommandPalette';
import { ShortcutsSheet } from './ShortcutsSheet';
import { GuideButton } from '../guide/GuideButton';
import { SplitButton, SplitFrame, forwardEmbeddedEvents, isEmbedded, setSplitUser } from './SplitView';
import { Bell as BellGlyph, LayoutGrid } from 'lucide-react';
import { UserMenu } from './UserMenu';
import { startImpersonation, getImpersonation } from '../auth/impersonation';
import { OfflineBanner } from './OfflineBanner';
import { ImpersonationBanner } from './ImpersonationBanner';
import { useProfile } from '../profile/ProfileProvider';
import { Bell } from '../notifications/Bell';
import { SeenBadge, setSeenUser, todoSeenKey, useSeenSet } from '../notifications/seen';
import { MyTeamNavItem } from '../myTeam/MyTeamNavItem';
import { FeatureAnnounce } from '../announcements/FeatureAnnounce';
import { AnnouncementPopup } from '../announcements/AnnouncementPopup';
import { FeedbackButton } from '../feedback/FeedbackButton';
import { HeaderClock } from '../timeTracking/HeaderClock';
import { PendingClockReplay } from '../timeTracking/PendingClockReplay';
import { ReviewCycleBanner } from '../review/ReviewCycleBanner';
import { refreshPushToken } from '../push/push';

/** The DAAily CRM test (sandbox) — a separate app, see github.com/DAAily/daaily-crm. */
const CRM_TEST_URL = 'https://daaily-crm-1119469304.europe-west6.run.app';

/** Sidebar row. Active = plum text on a plum tint with a 2px bar on the sidebar edge. */
const navItemCls = (active: boolean) =>
  [
    'relative flex items-center gap-2.5 h-8 px-2.5 rounded-md text-sm tracking-normal transition-colors duration-fast',
    active
      ? "bg-brand-subtle text-brand font-medium before:absolute before:-left-3 before:top-1.5 before:bottom-1.5 before:w-0.5 before:rounded-full before:bg-brand before:content-['']"
      : 'text-fg-muted hover:bg-surface-2 hover:text-fg',
  ].join(' ');
const countBadgeCls = 'ml-auto inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-brand-solid text-white text-2xs font-semibold tabular-nums';
const dotBadgeCls = 'absolute top-1 right-1 w-2 h-2 rounded-full bg-brand-solid';

/** The DAAily lockup, switched by theme (never recoloured with a filter). */
function Lockup({ className }: { className?: string }) {
  return (
    <>
      <img src="/brand/daaily-lockup-ink.png" alt="designboom, ArchDaily, DAAily platforms" className={`dark:hidden ${className ?? ''}`} />
      <img src="/brand/daaily-lockup-paper.png" alt="designboom, ArchDaily, DAAily platforms" className={`hidden dark:block ${className ?? ''}`} />
    </>
  );
}

/** Square mark for the collapsed rail and mobile bar: the hub app icon (Option A, "The Hub", Sep 2026). */
function Mark({ className }: { className?: string }) {
  return <img src="/icon-192.png" alt="DAAily Hub" className={`rounded-md ${className ?? ''}`} />;
}

export function Layout({ children }: { children: ReactNode }) {
  const { state } = useProfile();
  // Re-render when the impersonation flag is re-seeded from the auth token
  // (restored custom-token session in a fresh tab — see syncImpersonationFromToken).
  const [, setImpSync] = useState(0);
  useEffect(() => {
    const h = () => setImpSync((x) => x + 1);
    window.addEventListener('daaily-impersonation-sync', h);
    return () => window.removeEventListener('daaily-impersonation-sync', h);
  }, []);
  // Desktop sidebar collapse (icons only) — persisted per browser.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('daaily.sidebarCollapsed') === '1'; } catch { return false; }
  });
  const toggleCollapsed = () =>
    setCollapsed((c) => {
      try { localStorage.setItem('daaily.sidebarCollapsed', c ? '0' : '1'); } catch { /* ignore */ }
      return !c;
    });
  // Label wrapper class — labels/badges hide when collapsed (icons keep the row height).
  const lbl = collapsed ? 'hidden' : 'truncate';
  // Tools with a Personal | Team sidebar sub-nav (shown while inside the tool;
  // clicking the parent again folds/unfolds). Team stays active across its
  // sibling team-side routes.
  const [foldedSub, setFoldedSub] = useState<Record<string, boolean>>({});
  const nav = useAppNav();
  const {
    SUB_NAVS, todos, teamCounts, teamSigs, onboardingSig, pathname, ready, isAdmin, tools, canManageMutations, isManager, commissionsInAdminNav, adminActive, navGroups,
  } = nav;

  // Per-user browser memory (seen badges, mobile pins, split view): keyed by the
  // signed-in email so impersonation or a shared machine never mixes people.
  const userEmail = state.status === 'ready' ? state.user.email.toLowerCase() : '';
  setSeenUser(userEmail);
  setSplitUser(userEmail);
  const visitsKey = `daaily.toolVisits:${userEmail}`;
  // Mobile pins: the two tools this device opens most (falls back to sidebar order).
  const allTools = navGroups.flatMap((g) => g.items);
  useEffect(() => {
    if (isEmbedded()) return; // the split pane doesn't count towards mobile pins
    const hit = allTools.find((t) => pathname === t.to || pathname.startsWith(`${t.to}/`));
    if (!hit) return;
    try {
      const v = JSON.parse(localStorage.getItem(visitsKey) || '{}') as Record<string, number>;
      v[hit.to] = (v[hit.to] ?? 0) + 1;
      localStorage.setItem(visitsKey, JSON.stringify(v));
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, visitsKey]);
  const pinned = (() => {
    let visits: Record<string, number> = {};
    try { visits = JSON.parse(localStorage.getItem(visitsKey) || '{}'); } catch { /* ignore */ }
    return allTools
      .map((t, i) => ({ t, i, n: visits[t.to] ?? 0 }))
      .sort((a, b) => b.n - a.n || a.i - b.i)
      .slice(0, 2)
      .map((x) => x.t);
  })();
  useEffect(() => forwardEmbeddedEvents(), []);
  // Re-register this device's push token if FCM rotated it (no-op when off).
  useEffect(() => { void refreshPushToken(); }, []);
  const seenSet = useSeenSet();
  const unseenTodos = todos.filter((t) => !seenSet.has(todoSeenKey(t))).length;

  const sidebarLinkClass = ({ isActive }: { isActive: boolean }) => navItemCls(isActive);
  // Group heading, or a hairline in the collapsed rail.
  const groupLabel = (label: string) => collapsed
    ? <div className="mx-1 my-2 border-t border-line" aria-hidden="true" />
    : <div className="px-2.5 pt-4 pb-1 text-2xs font-semibold uppercase text-fg-faint">{label}</div>;

  // One renderer for both sidebar tool sections — includes the Personal|Team
  // sub-nav (SUB_NAVS) with fold-on-parent-click, for whichever tools have it.
  const renderToolItem = (item: NavItem) => {
    const teamN = SUB_NAVS[item.to] ? (teamCounts[item.to] ?? 0) : 0;
    const teamSig = teamSigs[item.to] ?? '';
    // Where the badged items live: the child carrying the count (Team/Admin
    // queue); visiting it clears the parent and child badge (one shared key).
    const isBadgeChild = (c: { to: string; label: string }) =>
      c.label === 'Admin' || (c.label === 'Team' && c.to !== '/expenses/team') || (item.to === '/signatures' && c.label === 'Personal');
    const queueRoute = SUB_NAVS[item.to]?.find(isBadgeChild)?.to ?? item.to;
    const badgeKey = teamN > 0 ? `${item.to}:${teamSig}` : `${item.to}:${onboardingSig}:${item.badge ?? 0}`;
    const subOpen = !!SUB_NAVS[item.to] && pathname.startsWith(item.to) && !collapsed && !foldedSub[item.to];
    return (
    <div key={item.to}>
      <NavLink
        to={item.to}
        className={SUB_NAVS[item.to]
          ? () => navItemCls(pathname.startsWith(item.to))
          : sidebarLinkClass}
        aria-label={collapsed ? `${item.label}${teamN > 0 || item.badge ? ` (${teamN || item.badge})` : ''}` : undefined}
        title={collapsed ? item.label : undefined}
        end={!!SUB_NAVS[item.to]}
        onClick={(e) => {
          if (!SUB_NAVS[item.to]) return;
          // Most SUB_NAVS parents (Time Tracking, Documents, Signatures, Outreach)
          // have no page of their own — their first child (e.g. "Personal") IS the
          // parent route. For those, clicking the parent while already inside should
          // just fold/unfold rather than re-navigate to a route we're already on.
          // Analytics is different: its landing page (`/analytics`) is real content,
          // deliberately left out of the sub-nav list, so no child aliases it — the
          // parent must stay a real link there, or it becomes unreachable once inside.
          const parentHasNoOwnPage = SUB_NAVS[item.to]!.some((c) => c.to === item.to);
          if (pathname.startsWith(item.to) && (parentHasNoOwnPage || pathname === item.to)) {
            e.preventDefault();
            setFoldedSub((f) => ({ ...f, [item.to]: !f[item.to] }));
          } else {
            setFoldedSub((f) => ({ ...f, [item.to]: false }));
          }
        }}
      >
        <Icon as={item.icon} />
        <span className={lbl}>{item.label}</span>
        {/* Team-actions count on the parent while its sub-nav is closed. Every
            badge hides once its tool has been visited (notifications/seen.ts). */}
        {teamN > 0 && !subOpen && !collapsed && (
          <SeenBadge badgeKey={badgeKey} markRoute={queueRoute} count={teamN} className={countBadgeCls} />
        )}
        {!!item.badge && !collapsed && (
          <SeenBadge badgeKey={badgeKey} markRoute={item.to} count={item.badge} className={countBadgeCls} />
        )}
        {/* Collapsed rail: a dot on the icon so pending actions stay visible. */}
        {(teamN > 0 || !!item.badge) && collapsed && (
          <SeenBadge badgeKey={badgeKey} markRoute={teamN > 0 ? queueRoute : item.to} count={teamN || item.badge || 0} className={dotBadgeCls} dot />
        )}
      </NavLink>
      {/* Personal | Team children while inside the tool. */}
      {subOpen && (
        <div className="ml-6 mt-0.5 space-y-0.5">
          {SUB_NAVS[item.to]!.map((c) => (
            <NavLink key={c.to} to={c.to} end={c.end} className={({ isActive }) =>
              `flex items-center h-7 rounded-md px-2.5 text-sm tracking-normal transition-colors duration-fast ${
                isActive || c.match.some((m) => pathname.startsWith(m)) ? 'text-fg font-medium bg-surface-2' : 'text-fg-muted hover:text-fg hover:bg-surface-2'
              }`}>
              <span className="truncate">{c.label}</span>
              {isBadgeChild(c) && teamN > 0 && (
                <SeenBadge badgeKey={badgeKey} markRoute={c.to} count={teamN} className={countBadgeCls} />
              )}
            </NavLink>
          ))}
        </div>
      )}
    </div>
    );
  };

  // Inside the split-view pane (a same-origin iframe of the hub): the page only,
  // no sidebar, top bar or mobile bar.
  if (isEmbedded()) {
    return (
      <AppNavContext.Provider value={nav}>
        <div className="min-h-screen bg-app"><OfflineBanner />{children}</div>
      </AppNavContext.Provider>
    );
  }

  return (
    <AppNavContext.Provider value={nav}>
    <div className="min-h-screen flex bg-app">
      {/* ── Desktop sidebar ─────────────────────────── */}
      <aside className={`hidden md:flex md:flex-col ${collapsed ? 'w-sidebar-collapsed' : 'w-sidebar'} shrink-0 bg-sidebar border-r border-line sticky top-0 h-screen z-dropdown transition-[width] duration-base`}>
        <div className={`flex items-center justify-between ${collapsed ? 'flex-col gap-2 px-2 pt-3 pb-2' : 'px-3 pt-3 pb-1'}`}>
          <Link to="/" aria-label="DAAily Hub home" className={collapsed ? '' : 'block flex-1 px-3'}>
            {collapsed ? <Mark className="w-8 h-8" /> : <Lockup className="w-full max-w-44" />}
          </Link>
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expand the navigation' : 'Collapse the navigation'}
            className="self-start inline-flex items-center justify-center w-8 h-8 rounded-md text-fg-faint hover:text-fg hover:bg-surface-2 transition-colors duration-fast"
          >
            <Icon as={collapsed ? PanelLeftOpen : PanelLeftClose} />
          </button>
        </div>
        <nav aria-label="Main" className="flex-1 overflow-y-auto px-3 pb-4 space-y-0.5">
          <NavLink to="/" end className={sidebarLinkClass} aria-label={collapsed ? 'Home' : undefined} title={collapsed ? 'Home' : undefined}>
            <Icon as={ICONS.home} />
            <span className={lbl}>Home</span>
          </NavLink>
          {isManager && <MyTeamNavItem className={sidebarLinkClass} collapsed={collapsed} />}
          <NavLink to="/guide" className={sidebarLinkClass} aria-label={collapsed ? 'Guide' : undefined} title={collapsed ? 'Guide' : undefined}>
            <Icon as={ICONS.guide} />
            <span className={lbl}>Guide</span>
          </NavLink>

          {navGroups.map((g) => (
            <div key={g.label} className="space-y-0.5">
              {groupLabel(g.label)}
              {g.items.map(renderToolItem)}
            </div>
          ))}

          {isAdmin && (
            <div className="space-y-0.5">
              {groupLabel('Admin')}
              {/* Single entry — the admin area has its own left sub-nav. */}
              <NavLink to="/admin" className={() => navItemCls(adminActive)} aria-label={collapsed ? 'Admin' : undefined} title={collapsed ? 'Admin' : undefined}>
                <Icon as={ICONS.admin} />
                <span className={lbl}>Admin</span>
              </NavLink>
              {/* Standalone items, same level as Admin (below it). */}
              <NavLink to="/admin/config" className={sidebarLinkClass} aria-label={collapsed ? 'Config' : undefined} title={collapsed ? 'Config' : undefined}>
                <Icon as={ICONS.config} />
                <span className={lbl}>Config</span>
              </NavLink>
              <NavLink to="/admin/fte" className={sidebarLinkClass} aria-label={collapsed ? 'FTE' : undefined} title={collapsed ? 'FTE' : undefined}>
                <Icon as={ICONS.fte} />
                <span className={lbl}>FTE</span>
              </NavLink>
              <NavLink to="/admin/mutations" className={sidebarLinkClass} aria-label={collapsed ? 'Monthly Mutations' : undefined} title={collapsed ? 'Monthly Mutations' : undefined}>
                <Icon as={ICONS.mutations} />
                <span className={lbl}>Monthly Mutations</span>
              </NavLink>
              {commissionsInAdminNav && (
                <NavLink to="/commissions" className={sidebarLinkClass} aria-label={collapsed ? 'Commissions' : undefined} title={collapsed ? 'Commissions' : undefined}>
                  <Icon as={ICONS.commissions} />
                  <span className={lbl}>Commissions</span>
                </NavLink>
              )}
              <NavLink to="/admin/performance-reviews" className={sidebarLinkClass} aria-label={collapsed ? 'Performance Reviews' : undefined} title={collapsed ? 'Performance Reviews' : undefined}>
                <Icon as={ICONS.performance} />
                <span className={lbl}>Performance Reviews</span>
              </NavLink>
              <NavLink to="/admin/onboarding" className={sidebarLinkClass} aria-label={collapsed ? 'On/Offboarding' : undefined} title={collapsed ? 'On/Offboarding' : undefined}>
                <Icon as={ICONS.onboarding} />
                <span className={lbl}>On/Offboarding</span>
              </NavLink>
              {renderToolItem({ to: '/document-vault', label: 'Document Vault', icon: ICONS.vault })}
              {/* Separate app (own repo, own database): the Salesforce status-quo rebuild test. */}
              <a href={CRM_TEST_URL} target="_blank" rel="noreferrer" className={navItemCls(false)} aria-label={collapsed ? 'CRM (test)' : undefined} title={collapsed ? 'CRM (test)' : 'Opens in a new tab'}>
                <Icon as={Building2} />
                <span className={lbl}>CRM (test)</span>
              </a>
            </div>
          )}
          {/* Hub-admins (HR) who aren't full admins still get Monthly Mutations. */}
          {ready && !isAdmin && canManageMutations && (
            <div className="space-y-0.5">
              {groupLabel('Admin')}
              <NavLink to="/admin/mutations" className={sidebarLinkClass} aria-label={collapsed ? 'Monthly Mutations' : undefined} title={collapsed ? 'Monthly Mutations' : undefined}>
                <Icon as={ICONS.mutations} />
                <span className={lbl}>Monthly Mutations</span>
              </NavLink>
            </div>
          )}
        </nav>
        {/* The signed-in user: profile, theme and sign out. */}
        {ready && (
          <div className={`py-2 border-t border-line ${collapsed ? 'px-1.5' : 'px-3'}`}>
            <UserMenu placement="sidebar" collapsed={collapsed} />
          </div>
        )}
      </aside>

      {/* ── Right column: top bar + banners + content ── */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header
          className="sticky top-0 z-header flex items-center justify-between gap-3 md:gap-4 bg-app/80 backdrop-blur border-b border-line px-4 md:px-8"
          // Reserve the iOS status-bar area (home-screen PWA, black-translucent):
          // the bar fills it so the clock/battery sit on the bar, content below.
          // Height = --app-header-h (styles/index.css): 44pt bar on phones, 56px from md.
          style={{
            paddingTop: 'env(safe-area-inset-top, 0px)',
            height: 'var(--app-header-h)',
          }}
        >
          <Link to="/" className="md:hidden" aria-label="DAAily Hub home">
            <Mark className="w-8 h-8" />
          </Link>
          <div className="flex-1" />
          {/* Phones search from the More tab; the bar is full with the clock and NEW chip. */}
          {ready && <div className="hidden md:block"><SearchButton /></div>}
          {state.status === 'ready' && (
            <div className="flex items-center gap-3 md:gap-4 text-sm">
              {tools?.timeTracking?.enabled && state.user.timeTracksOwn !== false && (
                <>
                  <HeaderClock email={state.user.email} />
                  <PendingClockReplay />
                </>
              )}
              <FeatureAnnounce />
              <GuideButton />
              <SplitButton />
              <FeedbackButton />
              <Bell />
              {state.status === 'ready' && (state.user.seedUsers?.length ?? 0) > 0 && !getImpersonation() && (
                <SeedUserMenu
                  seeds={state.user.seedUsers!}
                  email={state.user.email}
                  name={state.user.displayName || state.user.email}
                />
              )}
              {/* Board deck — Michael only (moved out of the sidebar Sep 2026;
                  kept reachable from the header in case it's needed again). */}
              {state.status === 'ready' && state.user.email.toLowerCase() === 'michael.leitner@daaily.com' && (
                <Link to="/board" className="text-fg-muted hover:text-fg transition-colors duration-fast hidden sm:inline">
                  Board
                </Link>
              )}
              {/* Sign out moved into the user menu (sidebar on desktop, here on mobile). */}
              <div className="md:hidden"><UserMenu placement="topbar" /></div>
            </div>
          )}
        </header>

        {/* Phones have no sidebar, so the current tool's sub-nav gets its own row. */}
        <MobileSubNav subNavs={SUB_NAVS} pathname={pathname} />

        <ImpersonationBanner />
        {/* Announcement pop-up (Oct 2026); not inside the split pane (that returns earlier). */}
        <AnnouncementPopup />

        <OfflineBanner />
        {/* "It's review cycle time" strip (Oct 2026) while the person has open surveys in a filling cycle. */}
        <ReviewCycleBanner />

        {/* Extra bottom padding on mobile so the fixed bottom bar can't cover content. */}
        <div className="flex-1 min-w-0 pb-24 md:pb-0 flex flex-col"><SplitFrame>{children}</SplitFrame></div>
      </div>

      {/* ── Mobile bottom tab bar (Sep 2026, Slack-style): Home, the two tools this
           device opens most, Activity (to-dos) and More (every other app). ── */}
      <nav
        aria-label="Main"
        className="md:hidden fixed bottom-0 left-0 right-0 z-header grid grid-cols-5 bg-sidebar border-t border-line"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <BottomTab to="/" icon={ICONS.home} label="Home" end />
        {pinned.map((it) => (
          <BottomTab key={it.to} to={it.to} icon={it.icon} label={mobileShortLabel(it.label)} matchPrefix={it.to} />
        ))}
        {Array.from({ length: Math.max(0, 2 - pinned.length) }, (_, i) => <span key={`pad${i}`} />)}
        <BottomTab to="/activity" icon={BellGlyph} label="Activity" matchPrefix="/activity" dot={unseenTodos > 0} />
        <BottomTab to="/more" icon={LayoutGrid} label="More" matchPrefix="/more" />
      </nav>
      {ready && <CommandPalette />}
      {ready && <ShortcutsSheet />}
    </div>
    </AppNavContext.Provider>
  );
}

/**
 * Mobile sub-nav (<md, where the sidebar is hidden): the current tool's
 * SUB_NAVS children (Personal | Team | Admin, the Analytics/Operations pages)
 * as a horizontally scrolling chip row under the top bar. Without it those
 * pages were unreachable on phones / the home-screen PWA. Same data as the
 * sidebar children, so visibility rules stay in one place (useAppNav).
 */
function MobileSubNav({ subNavs, pathname }: { subNavs: ReturnType<typeof useAppNav>['SUB_NAVS']; pathname: string }) {
  const root = Object.keys(subNavs).find((k) => pathname === k || pathname.startsWith(`${k}/`));
  const children = root ? subNavs[root] ?? [] : [];
  // Tools whose landing page isn't one of the children (Analytics' index) get
  // an Overview chip so the landing stays reachable from inside the tool.
  const items = root && children.length > 0 && !children.some((c) => c.to === root)
    ? [{ to: root, label: 'Overview', end: true, match: [] as string[] }, ...children]
    : children;
  const rowRef = useRef<HTMLDivElement>(null);
  // Keep the active chip in view (Analytics has ~20 pages).
  useEffect(() => {
    rowRef.current?.querySelector('[aria-current="page"]')?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [pathname]);
  if (items.length < 2) return null;
  return (
    <nav aria-label="Section" className="md:hidden border-b border-line bg-app">
      <div ref={rowRef} className="flex gap-1.5 overflow-x-auto px-4 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map((c) => {
          const forced = c.match.some((m) => pathname.startsWith(m));
          return (
            <NavLink
              key={c.to}
              to={c.to}
              end={c.end}
              className={({ isActive }) =>
                `shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors duration-fast ${
                  isActive || forced ? 'bg-surface-3 text-fg font-medium' : 'bg-surface-2 text-fg-muted'
                }`}
            >
              {c.label}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}

// Short labels for the compact mobile tab bar.
const MOBILE_SHORT_LABELS: Record<string, string> = {
  'Expense Tool': 'Expenses',
  'Invoice Submission': 'Invoices',
  'Credit Cards': 'Cards',
  'Share credit card receipt': 'Share',
  'Time Tracking': 'Time',
  'Absence Management': 'Absence',
  'Org Chart': 'Org',
  'DAAily Drivers': 'Drivers',
  'Performance Reviews': 'Reviews',
  'Subscriptions': 'Subs',
  'Documents': 'Docs',
  'Projects': 'Projects',
};
const mobileShortLabel = (label: string) => MOBILE_SHORT_LABELS[label] ?? label;

function BottomTab({
  to,
  icon,
  label,
  end,
  matchPrefix,
  dot,
}: {
  to: string;
  icon: LucideIcon;
  label: string;
  end?: boolean;
  matchPrefix?: string;
  /** Plum dot on the icon (unseen items). */
  dot?: boolean;
}) {
  const location = useLocation();
  const active = matchPrefix
    ? location.pathname.startsWith(matchPrefix)
    : end
      ? location.pathname === to
      : location.pathname.startsWith(to);
  return (
    <Link
      to={to}
      className={[
        'relative flex min-w-0 flex-col items-center justify-center gap-0.5 py-2 text-2xs !tracking-normal !normal-case transition-colors duration-fast',
        active ? 'text-brand font-semibold' : 'text-fg-muted',
      ].join(' ')}
      aria-current={active ? 'page' : undefined}
    >
      <span className="relative">
        <Icon as={icon} size={20} />
        {dot && <span className="absolute -top-0.5 -right-1 w-2 h-2 rounded-full bg-brand-solid" aria-label="New" />}
      </span>
      <span className="max-w-full px-1 truncate">{label}</span>
    </Link>
  );
}

/**
 * "Seed user" top-bar menu — shown only to a tester whose own user doc lists
 * `seedUsers` (sandbox accounts with `seedOwnerEmail` = them). Picking one
 * starts TRUE impersonation (adminImpersonate allows the seed's owner); the
 * red ImpersonationBanner + "Stop impersonating" then applies as usual.
 */
function SeedUserMenu({ seeds, email, name }: { seeds: string[]; email: string; name: string }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const start = (target: string) => {
    setErr(null);
    setBusy(target);
    startImpersonation({ targetEmail: target, adminEmail: email, adminName: name })
      .then(() => window.location.assign('/'))
      .catch((e: unknown) => {
        setErr(e instanceof Error ? e.message : 'Could not switch.');
        setBusy(null);
      });
  };
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-fg-muted hover:text-fg transition-colors duration-fast"
      >
        Seed user
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-72 rounded-lg border border-line bg-surface text-fg shadow-md z-dropdown p-1">
          <p className="px-3 pt-2 pb-1 text-xs text-fg-muted">
            Switch into a test account (use “Stop impersonating” to come back):
          </p>
          {seeds.map((s) => (
            <button
              key={s}
              type="button"
              disabled={busy != null}
              onClick={() => start(s)}
              className="w-full text-left px-3 py-2 text-sm text-fg-muted hover:text-fg hover:bg-surface-2 rounded-md disabled:opacity-50"
            >
              {busy === s ? 'Switching…' : s}
            </button>
          ))}
          {err && <p className="px-3 py-2 text-xs text-danger">{err}</p>}
        </div>
      )}
    </div>
  );
}
